<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container-fluid mt-4" style="max-width:1860px; margin:0 auto">
    <!-- Title left, page actions right — the same header as Bordbookinger, which is where a user of
         this app has learned to look for them. -->
    <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
      <h3 class="mb-0">Ordrer</h3>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <span v-if="refreshedAt" class="orders-head__stamp">Sist oppdatert {{ refreshedAt }}</span>
        <!-- Deliberately a plain button and not a poll. A page that refreshes itself invites a
             merchant to treat it as an alarm, and an alarm that only works while a tab is open is a
             promise we would break on the busiest Friday. This says what it does: fetch the list
             again, now. -->
        <button type="button" class="bo_btn_secondary" :disabled="loading" @click="refreshOrders">
          <font-awesome-icon :icon="['fas', 'sync-alt']" :spin="loading" class="me-1" />
          {{ loading ? 'Oppdaterer…' : 'Oppdater' }}
        </button>
      </div>
    </div>

    <!--
      Shown only to a merchant who can take orders and owns no terminal. That is the exact state in
      which an order can arrive with nothing to announce it, and it ends the day they have a till —
      so it is a statement about today, not a step on a list that will not go away.
    -->
    <div v-if="showNoTerminalNotice" class="orders-notice mb-3">
      <font-awesome-icon :icon="['fas', 'info-circle']" class="me-2" />
      <span>
        Ingenting varsler deg om nye bestillinger. Du må selv oppdatere denne ordrer-siden for å se om nye bestillinger.
        <a :href="storeUrl" target="_blank" rel="noopener">Se utstyr →</a>
      </span>
    </div>

    <div v-if="hasMultiLocations()" class="select-location-box mt-4 mb-3">
      <span class="ingredient-title">For avdeling</span>
      <select class="form-select" v-model="locationId" @change="onLocationChange()">
        <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
      </select>
    </div>

    <!-- Search & Filters -->
    <div class="filter-bar mb-3">
      <div class="d-md-none filter-toggle" @click="filtersExpanded = !filtersExpanded">
        <font-awesome-icon :icon="['fas', 'filter']" class="me-1" style="font-size: 22px; color: var(--bo-muted-2);" />
        <span>Søk & Filter</span>
        <span v-if="hasActiveFilters" class="badge bg-primary ms-1">{{ activeFilterCount }}</span>
        <font-awesome-icon :icon="['fas', filtersExpanded ? 'angle-up' : 'angle-down']" class="ms-auto" style="font-size: 28px; color: #ECB109;" />
      </div>
      <div :class="{ 'd-none d-md-block': !filtersExpanded }">
        <div class="d-flex flex-wrap gap-2 align-items-end">
          <div class="filter-item filter-search">
            <input
              type="text"
              class="form-control"
              placeholder="Søk etter ordrenr, navn eller telefon..."
              v-model="searchQuery"
              @input="onFilterChange()"
            />
          </div>
          <div class="filter-item">
            <select class="form-select" v-model="filterChannel" @change="onDropdownFilterChange()">
              <option value="">Alle kanaler</option>
              <option value="WEBSHOP">Nettbutikk</option>
              <option value="KIOSK">Selvbetjeningskiosk</option>
              <option value="POS">Kasse</option>
            </select>
          </div>
          <div class="filter-item">
            <select class="form-select" v-model="filterType" @change="onDropdownFilterChange()">
              <option value="">Alle typer</option>
              <option value="DINE_IN">Spise her</option>
              <option value="PICKUP">Ta med</option>
              <option value="DELIVERY">Levere</option>
            </select>
          </div>
          <div class="filter-item">
            <select class="form-select" v-model="filterStatus" @change="onDropdownFilterChange()">
              <option value="">Alle statuser</option>
              <option value="ORDERED">Bestilt</option>
              <option value="CONFIRMED">Bekreftet</option>
              <option value="COMPLETED">Fullført</option>
              <option value="CANCELLED">Kansellert</option>
            </select>
          </div>
          <div class="filter-item">
            <select class="form-select" v-model="filterPaymentStatus" @change="onDropdownFilterChange()">
              <option value="">Alle betalingsstatus</option>
              <option value="PAID">Betalt</option>
              <option value="NOT_PAID">Ikke betalt</option>
            </select>
          </div>
          <div class="filter-item filter-item--date">
            <Datepicker v-model="filterDateFrom" :locale="nbLocale" inputFormat="dd.MM.yyyy" placeholder="Fra dato" class="form-control" @update:modelValue="onDropdownFilterChange()" />
          </div>
          <div class="filter-item filter-item--date">
            <Datepicker v-model="filterDateTo" :locale="nbLocale" inputFormat="dd.MM.yyyy" placeholder="Til dato" class="form-control" @update:modelValue="onDropdownFilterChange()" />
          </div>
        </div>
      </div>
      <div v-if="hasActiveFilters" class="mt-2">
        <button class="btn btn-sm btn-outline-secondary" @click="clearFilters()">Fjern alle filtre</button>
        <span class="ms-2 text-muted" style="font-size: 14px">{{ orders.length }} ordre{{ orders.length !== 1 ? 'r' : '' }} funnet</span>
      </div>
    </div>

    <!-- Error alert -->
    <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      {{ errorMessage }}
      <button type="button" class="btn-close" @click="errorMessage = ''"></button>
    </div>

    <div class="orders-grid" ref='table'>
      <div v-for="order in orders" :key="order.id" @click="open(order.id)">
        <div class="order-card" :class="'border-status-' + (order.orderStatus ? order.orderStatus.toLowerCase() : '')">
          <!-- Header -->
          <div class="order-card-header">
            <div>
              <span v-if="hasMultiLocations()" class="order-location">{{ order.locationName }}</span>
              <div class="order-title">
                <span class="order-type-label">{{ order.type }}</span>
                <span class="order-id">Ordre #{{ order.orderId }}</span>
              </div>
              <div class="order-time">Bestilt: {{ order.orderedTime }}</div>
              <div v-if="order.pickupOrDeliveryTimeText" class="order-pickup-time">
                <font-awesome-icon :icon="['fas', 'clock']" class="me-1" />
                {{ order.rawOrderType === 'DELIVERY' ? 'Levering' : 'Henting' }}: <span class="order-pickup-time-value">{{ order.pickupOrDeliveryTimeText }}</span>
              </div>
            </div>
            <div class="order-chevron">
              <font-awesome-icon :icon="['fas', 'angle-right']" />
            </div>
          </div>

          <div class="order-card-divider"></div>

          <!-- Customer info -->
          <div class="order-card-body">
            <div v-if="order.name" class="order-info-row">
              <span class="order-label">Navn</span>
              <span class="order-value order-name">{{ order.name }}</span>
            </div>
            <div v-if="order.mobileNumber" class="order-info-row">
              <span class="order-label">Telefon</span>
              <span class="order-value">{{ order.mobileNumber }}</span>
            </div>
            <div v-if="order.tableName" class="order-info-row">
              <span class="order-label">Bord</span>
              <span class="order-value">{{ order.tableName }}</span>
            </div>
            <div v-if="order.presentCounter" class="order-info-row">
              <span class="order-label">Bestillingsnr</span>
              <span class="order-value order-present-counter">{{ order.presentCounter }}</span>
            </div>

            <!-- Status & Payment badges -->
            <div class="order-badges">
              <span class="order-badge" :class="'badge-' + (order.orderStatus ? order.orderStatus.toLowerCase() : '')">{{ order.statusText }}</span>
              <span class="order-badge" :class="'badge-' + OrderUtil.getPaymentBadgeKind(order.paymentStatus, order.orderStatus)">
                <font-awesome-icon :icon="order.paymentDisplay.icon" class="me-1" />{{ order.paymentDisplay.text }}
              </span>
              <span v-if="order.refundStatus" class="order-badge" :class="order.refundStatus === 'Refundert' ? 'badge-refunded' : 'badge-partial-refund'">{{ order.refundStatus }}</span>
              <span v-if="order.splitFromOrderId" class="order-badge badge-split">Delregning {{ order.splitPart }} av #{{ order.splitFromOrderId }}</span>
              <span v-else-if="order.splitPartCount > 0" class="order-badge badge-split">{{ order.splitPartCount }} {{ order.splitPartCount === 1 ? 'delregning' : 'delregninger' }}</span>
            </div>

            <!-- Meta row -->
            <div class="order-meta">
              <div v-if="order.servedBy" class="order-meta-item">
                <span class="order-label">Betjent av</span>
                <span>{{ order.servedBy }}</span>
              </div>
              <div class="order-meta-item">
                <span class="order-label">Kanal</span>
                <span>{{ order.channel }}</span>
              </div>
            </div>
          </div>

          <!-- Footer: Total -->
          <div class="order-card-footer">
            <span class="order-label">Totalbeløp</span>
            <span class="order-total">{{ order.toPayAmount }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!loading && orders.length === 0 && hasActiveFilters" class="text-center text-muted mt-4">
      <p>Ingen ordrer samsvarer med filtrene dine.</p>
    </div>

    <p v-if='orders.length' id='load-more' :class='loadMoreStatus'></p>
  </div>

  <SidePanel v-model="isOpened" lock-scroll close-on-overlay width="580px">
    <div v-if="dataForOrderDetail">
      <OrderDetail :dataForOrderDetail="dataForOrderDetail" @close="close()" @open-order="open"/>
    </div>
  </SidePanel>
</template>

<!-- infinite scroll: https://mitya.uk/articles/implementing-infinite-scroll-lazy-loading-vue-3 -->
<script setup>
import SidePanel from "@/components/other/SidePanel.vue";
import OrderDetail from "@/components/order/OrderDetail.vue";
import LoadingSpinner from "@/components/other/LoadingSpinner";
import OrderUtil from "@/util/OrderUtil";
import LocationUtil from "@/util/LocationUtil";
import MathUtil from "@/util/MathUtil";
import {onMounted, onUnmounted, ref, computed} from "vue";
import OrderService from "@/services/order/OrderService";
import LocationService from "@/services/location/LocationService";
import TerminalService from "@/services/terminal/TerminalService";
import TerminalVisibility from "@/util/TerminalVisibility";
import Datepicker from '@/components/other/ToggleDatepicker.vue';
import {nb} from "date-fns/locale";

const nbLocale = nb;

const loading = ref(true);
const page = ref(1);
const limit = 50;
const locationId = ref('');
const locations = ref([]);
const orders = ref([]);
const table = ref(null);
const errorMessage = ref('');
const refreshedAt = ref('');

/*
 * «Nothing tells you an order arrived.»
 *
 * True in exactly one state: this location can take orders online, and has no till to print them. Both
 * halves matter.
 *
 * And there is no software answer to fall back on. Browser notifications need a user gesture to
 * unlock sound and go silent the moment the tab closes; FCM push was tried on the fleet and was
 * sometimes TEN MINUTES late, which is why the devices poll and print instead. So the honest thing
 * the page can do is say that nothing is watching. Without payment activated no order can arrive, so the notice would be a nag about a
 * risk that does not exist yet; with a terminal the order prints itself and there is nothing to say.
 *
 * It ends the day they have a till, which is what separates it from a checklist step that never goes
 * away. Asked per location, because a merchant with two venues can have a till in one of them.
 */
const canTakeOrders = ref(false);
const terminalCount = ref(-1);   // -1 = not answered yet; never show the notice on a guess
const showNoTerminalNotice = computed(() => canTakeOrders.value && terminalCount.value === 0);
/* Absolute: the shop is a different app. Host confirmed answering 200 on 2026-09-30. */
const storeUrl = import.meta.env.PROD ? 'https://cagista.com/store' : 'http://localhost:3030/store';

async function checkOrderAlerting() {
  canTakeOrders.value = false;
  terminalCount.value = -1;
  if (!locationId.value) {
    return;
  }
  try {
    const [location, terminals] = await Promise.all([
      LocationService.getLocation(locationId.value),
      TerminalService.getTerminalsForLocation(locationId.value)
    ]);
    canTakeOrders.value = (location.data || {}).onlinePaymentProvider === 'VIVA';
    terminalCount.value = TerminalVisibility.forMerchantView(terminals.data).length;
  } catch (e) {
    // A notice is a nice-to-have. An unreadable answer leaves it hidden rather than guessing wrong in
    // either direction — telling a merchant who owns a till that nothing will tell them is worse than
    // saying nothing at all.
    console.log(e);
  }
}

// Search & filter state
const searchQuery = ref('');
const filterStatus = ref('');
const filterType = ref('');
const filterChannel = ref('');
const filterPaymentStatus = ref('');
const filterDateFrom = ref(null);
const filterDateTo = ref(null);

const filtersExpanded = ref(false);

const dataForOrderDetail = ref(null);
const isOpened = ref(false);

const hasActiveFilters = computed(() => {
  return searchQuery.value || filterStatus.value || filterType.value || filterChannel.value || filterPaymentStatus.value || filterDateFrom.value || filterDateTo.value;
});

const activeFilterCount = computed(() => {
  let count = 0;
  if (searchQuery.value) count++;
  if (filterStatus.value) count++;
  if (filterType.value) count++;
  if (filterChannel.value) count++;
  if (filterPaymentStatus.value) count++;
  if (filterDateFrom.value) count++;
  if (filterDateTo.value) count++;
  return count;
});

let searchDebounceTimer = null;

function getFilterParams() {
  const filters = {};
  if (searchQuery.value) filters.searchQuery = searchQuery.value;
  if (filterStatus.value) filters.orderStatus = filterStatus.value;
  if (filterType.value) filters.orderType = filterType.value;
  if (filterChannel.value) filters.channel = filterChannel.value;
  if (filterPaymentStatus.value) filters.paymentStatus = filterPaymentStatus.value;
  if (filterDateFrom.value) {
    const from = new Date(filterDateFrom.value);
    from.setHours(0, 0, 0, 0);
    filters.dateFrom = from.toISOString();
  }
  if (filterDateTo.value) {
    const to = new Date(filterDateTo.value);
    to.setHours(23, 59, 59, 999);
    filters.dateTo = to.toISOString();
  }
  return filters;
}

function onFilterChange() {
  // Debounce search input, immediate for dropdowns/dates
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    reloadWithFilters();
  }, 400);
}

function onDropdownFilterChange() {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  reloadWithFilters();
}

/**
 * Fetch the list again, keeping the filters. Separate from reloadWithFilters only in what it means to
 * the merchant — that one is «you changed a filter», this one is «show me what has come in since».
 */
async function refreshOrders() {
  await reloadWithFilters();
  refreshedAt.value = new Date().toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
}

async function reloadWithFilters() {
  loading.value = true;
  orders.value = [];
  page.value = 1;
  loadMoreStatus.value = 'loading';
  await loadData();
}

function clearFilters() {
  searchQuery.value = '';
  filterStatus.value = '';
  filterType.value = '';
  filterChannel.value = '';
  filterPaymentStatus.value = '';
  filterDateFrom.value = null;
  filterDateTo.value = null;
  reloadWithFilters();
}

function open(orderDocumentId){
  loading.value = true;
  errorMessage.value = '';
  OrderService.getDataForOrderDetailGui(orderDocumentId).then(
      (response) => {
        dataForOrderDetail.value = response.data;
        loading.value = false;
        isOpened.value = true;
      },
      (error) => {
        loading.value = false;
        errorMessage.value = 'Kunne ikke hente ordredetaljer. Vennligst prøv igjen.';
        console.log(error);
      }
  );
}

function close(){
  isOpened.value = false;
}

async function onLocationChange(){
  // Save selected location to localStorage
  localStorage.setItem('orders_selected_location', locationId.value);
  // The other venue may have a till where this one does not.
  checkOrderAlerting();

  loading.value = true;
  errorMessage.value = '';
  //Reset related
  orders.value = [];
  page.value = 1;
  loadMoreStatus.value = 'loading';
  await loadData();
}

async function loadData(){
  try {
    const response = await OrderService.listOrders(locationId.value, limit, page.value, getFilterParams());
    const ordersArray = response.data;

    for (let i = 0; i < ordersArray.length; i++) {
      let order = ordersArray[i];
      orders.value.push({
        'locationName': getLocationName(order.locationId),
        'id': order.id,
        'orderId' : order.orderId,
        'name': getNameText(order.name),
        'mobileNumber': getPhoneNrText(order.mobileNumber),
        'orderStatus': order.orderStatus,
        'statusText' : OrderUtil.getOrderStatusText(order.orderStatus),
        'paymentStatus' : OrderUtil.getPaymentStatusText(order.payments),
        'paymentDisplay' : OrderUtil.getPaymentDisplay(order.payments),
        'refundStatus' : OrderUtil.getRefundStatusText(order.refundedCartItems, order.cartItems),
        // «Betal hver for seg»: a guest's part of a table's bill, or the table's order its parts were paid off
        'splitPart' : order.splitFrom ? order.splitFrom.part : null,
        'splitFromOrderId' : order.splitFrom ? order.splitFrom.orderId : null,
        'splitPartCount' : order.splitInto ? order.splitInto.length : 0,
        'type' : OrderUtil.getOrderTypeText(order.orderType),
        'rawOrderType' : order.orderType,
        'rawOrderedTime' : order.orderedTime,
        'tableName' : order.tableName,
        'presentCounter' : useOrderDisplay(order.locationId) ? order.presentCounter : null,
        'servedBy' : order.servedBy,
        'channel' : OrderUtil.getChannelText(order),
        'orderedTime' : OrderUtil.getDateTimeText(order.orderedTime),
        'pickupOrDeliveryTimeText' : getPickupOrDeliveryTimeText(order),
        'totalAmount' : order.totalAmount,
        'toPayAmount' : MathUtil.displayTwoDecimals(getDisplayToPayAmount(order))
      });
    }
    loadMoreStatus.value = ordersArray.length < limit ? 'no-more' : 'idle';
    page.value++;
    loading.value = false;
    errorMessage.value = '';
  } catch (error) {
    loading.value = false;
    loadMoreStatus.value = 'idle';
    errorMessage.value = 'Kunne ikke laste ordrer. Vennligst prøv igjen.';
    console.log(error);
  }
}

//infinite scroll
const loadMoreStatus = ref('idle');
let scrollCb = null;

onMounted(() => {
  locations.value = LocationUtil.getLocations();

  // Restore saved location from localStorage or use first location as default
  const savedLocationId = localStorage.getItem('orders_selected_location');
  const isValidSavedLocation = savedLocationId && locations.value.find(loc => loc.id === savedLocationId);
  locationId.value = isValidSavedLocation ? savedLocationId : (locations.value.length ? locations.value[0].id : '');
  checkOrderAlerting();

  scrollCb = async () => {
    if (loadMoreStatus.value === 'idle' && table.value && table.value.getBoundingClientRect().bottom < window.innerHeight) {
      loadMoreStatus.value = 'loading';
      await loadData();
    }
  };
  window.addEventListener('scroll', scrollCb);
  scrollCb();
});

onUnmounted(() => {
  if (scrollCb) {
    window.removeEventListener('scroll', scrollCb);
  }
});

function hasMultiLocations(){
  return LocationUtil.hasMultiLocations();
}

function getDisplayToPayAmount(order){
  let manualDiscount = order.manualDiscount ? order.manualDiscount : 0;
  let tipsAmount = order.tipsAmount ? order.tipsAmount : 0;
  return order.totalAmount + tipsAmount - manualDiscount;
}

function getLocationName(locationId){
  let location = LocationUtil.getLocation(locationId);
  return location.name;
}

function useOrderDisplay(locationId){
  let location = LocationUtil.getLocation(locationId);
  return location !== undefined && location.useOrderDisplay === true;
}

function getNameText(name){
  return OrderUtil.getNameText(name);
}

function getPhoneNrText(mobileNumber){
  return OrderUtil.getPhoneNrText(mobileNumber);
}

function getPickupOrDeliveryTimeText(order){
  if (order.pickupOrDeliveryTime) {
    return OrderUtil.getDateTimeTextShort(order.pickupOrDeliveryTime);
  } else if (order.orderType === 'PICKUP') {
    if (order.pickupTimeOption === 'whenFoodIsReady') {
      return 'Får melding når maten er klar';
    }
  } else if (order.orderType === 'DELIVERY') {
    if (order.deliveryTimeOption === 'asSoonAsPossible') {
      return 'Så fort som mulig';
    }
  }
  return null;
}

</script>

<style scoped>
.orders-head__stamp { font-size: 0.8rem; color: var(--bo-muted); }

/* Information, not a fault: nothing is broken, there is simply nothing to announce an order. Muted
   and level, so it does not read as an error the merchant has to fix before carrying on. */
.orders-notice {
  display: flex;
  align-items: baseline;
  padding: 10px 12px;
  border: 1px solid var(--bo-line);
  border-left: 3px solid #ECB109;
  border-radius: 6px;
  background: var(--bo-surface-2);
  font-size: 0.9rem;
  color: var(--bo-ink-2);
}

.orders-notice a { color: var(--bo-brand-ink); white-space: nowrap; }

/* Orders grid */
.orders-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 12px;
}

@media (min-width: 576px) {
  .orders-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (min-width: 992px) {
  .orders-grid { grid-template-columns: repeat(3, 1fr); }
}

@media (min-width: 1400px) {
  .orders-grid { grid-template-columns: repeat(4, 1fr); }
}

@media (min-width: 1800px) {
  .orders-grid { grid-template-columns: repeat(5, 1fr); }
}

/* Order card */
.order-card {
  background: var(--bo-surface);
  border-radius: 10px;
  border: 1px solid var(--bo-line);
  border-left: 4px solid var(--bo-muted-2);
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;
  overflow: hidden;
}

.order-card:hover {
  box-shadow: 0 6px 16px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

/*
 * Order status colors:
 *   ORDERED   — yellow: needs attention
 *   CONFIRMED — green: being prepared
 *   COMPLETED — no color (default var(--bo-muted-2)): done, out of focus
 *   CANCELLED — red: problem
 */
.border-status-ordered { border-left-color: #f59e0b; }
.border-status-confirmed { border-left-color: #22c55e; }
.border-status-completed { border-left-color: var(--bo-muted-2); }
.border-status-cancelled { border-left-color: #ef4444; }
.border-status-draft { border-left-color: #f59e0b; } /* «Åpen»: still open on the till */

/* Card header */
.order-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 16px 10px;
}

.order-location {
  font-size: 12px;
  color: var(--bo-muted);
  display: block;
  margin-bottom: 2px;
}

.order-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-type-label {
  text-transform: uppercase;
  font-size: 11px;
  font-weight: 600;
  background: var(--bo-surface-2);
  color: var(--bo-ink);
  padding: 2px 8px;
  border-radius: 4px;
}

.order-id {
  font-weight: 600;
  font-size: 15px;
  color: var(--bo-ink);
}

.order-time {
  font-size: 13px;
  color: var(--bo-ink);
  margin-top: 4px;
}

.order-pickup-time {
  font-size: 13px;
  color: var(--bo-ink);
  margin-top: 3px;
}

.order-pickup-time-value {
  font-weight: 600;
}

.order-chevron {
  font-size: 24px;
  color: #ECB109;
  display: flex;
  align-items: center;
  padding-top: 4px;
}

/* Divider */
.order-card-divider {
  height: 1px;
  background: var(--bo-surface-2);
  margin: 0 16px;
}

/* Card body */
.order-card-body {
  padding: 10px 16px;
}

.order-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 0;
}

.order-label {
  font-size: 13px;
  color: var(--bo-muted-2);
}

.order-value {
  font-weight: 600;
  font-size: 15px;
  color: var(--bo-ink);
}

.order-name {
  font-size: 17px;
}

.order-present-counter {
  font-size: 20px;
  font-weight: 700;
}

/* Badges */
.order-badges {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.order-badge {
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 12px;
}

.badge-ordered { background: var(--bo-warn-bg); color: var(--bo-warn-fg); }
.badge-confirmed { background: var(--bo-ok-bg); color: var(--bo-ok-fg); }
.badge-completed { background: var(--bo-surface-2); color: var(--bo-ink); }
.badge-cancelled { background: var(--bo-bad-bg); color: var(--bo-bad-fg); }
.badge-paid { background: var(--bo-ok-bg); color: var(--bo-ok-fg); }
.badge-unpaid { background: var(--bo-bad-bg); color: var(--bo-bad-fg); }
.badge-refunded { background: var(--bo-bad-bg); color: var(--bo-bad-fg); }
.badge-partial-refund { background: var(--bo-warn-bg); color: var(--bo-warn-fg); }
.badge-split { background: var(--bo-surface-2); color: var(--bo-ink); border: 1px solid var(--bo-line); }

/* Meta row */
.order-meta {
  display: flex;
  gap: 16px;
  margin-top: 8px;
}

.order-meta-item {
  display: flex;
  flex-direction: column;
  font-size: 13px;
  color: var(--bo-ink);
}

.order-meta-item .order-label {
  font-size: 11px;
  margin-bottom: 1px;
}

/* Card footer */
.order-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: var(--bo-surface-2);
  border-top: 1px solid var(--bo-surface-2);
}

.order-total {
  font-size: 18px;
  font-weight: 700;
  color: var(--bo-ink);
}

/* Infinite scroll */
#load-more {
  text-align: center;
  color: var(--bo-muted-2);
}

#load-more.loading::before {
  content: '';
  margin-top: 8px;
  width: 35px;
  height: 35px;
  border: solid 2px var(--bo-muted);
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

#load-more.no-more::before {
  content: '';
}

@keyframes rotation {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Filter bar */
.filter-bar {
  background-color: var(--bo-surface-2);
  border-radius: 8px;
  border: 1px solid var(--bo-line);
  padding: 12px;
}

.filter-toggle {
  display: flex;
  align-items: center;
  padding: 8px 4px;
  cursor: pointer;
  user-select: none;
}

.filter-item {
  min-width: 150px;
  flex: 1;
}

.filter-search {
  flex: 2;
  min-width: 250px;
}

/*
 * The calendar directly under its own date field, its right edge on the field's right edge (2026-10-13).
 * It was `position: fixed; right: 16px` — pinned to the WINDOW, so it opened at the far right of the screen
 * whichever field was clicked, nowhere near «Fra dato». On a TOUCH screen it was worse: touch.css sets
 * `top: calc(100% + 1px)` for a popout under its field, and against a fixed box 100% is the whole viewport —
 * so the calendar opened one pixel below the bottom of the screen and nothing seemed to happen (Samsung
 * Galaxy S21, and the same for the Backoffice Android app's WebView and the Sunmi devices). Measured in
 * Chromium with touch emulation: top 901 in a 900-high window.
 *
 * The library makes .v3dp__datepicker `position: relative`, and here it is exactly as wide as the field (the
 * input carries .form-control), so `right: 0` is the field's right edge and 100% its bottom edge. Right-aligned
 * so it opens inwards. This scoped rule outranks touch.css's popout rule (a :deep selector carries the
 * component's attribute), which anchors it the same way; touch.css still sets the wider touch width.
 */
.filter-item :deep(.v3dp__popout) {
  top: calc(100% + 4px);
  right: 0;
  left: auto;
}

/*
 * Below md the filters wrap two or three to a row and «Fra dato» lands in a LEFT column: a calendar
 * right-aligned under it is wider than the field and would leave the screen on the left, Monday first. A row
 * of its own for each date field puts both right edges on the page gutter, which is what makes it safe.
 */
@media (max-width: 767.98px) {
  .filter-item--date {
    flex-basis: 100%;
  }
}
</style>
